import { Base } from "../../framework/base";
import { Dictionary, IDictionary } from "./dictionary";

export interface IIdTitleNode {
    id: string;
    title: string;
    comment: string;
    active: boolean;
    children: IIdTitleNode[];
    attributes: IDictionary<string>;

    getTitle(): string;
    getAttributeValue(key: string): string;
    hasChildren(): boolean;
    getById(id: string): IIdTitleNode;
    getByIds(ids: string[]): IIdTitleNode[];
    getFirstActiveNode(): IIdTitleNode;
    getChildIds(): string[];
    getActiveChildIds(): string[];
    setHierarchialTitle(prefix: string);
    containsAllChildrenIds(ids: string[]): boolean;
    containsSomeChildrenIds(ids: string[]): boolean;
    removeIds(ids: string[]): IIdTitleNode;
    getAsList(list: IIdTitleNode[]): IIdTitleNode[];
    getChildIdPath(): string[];
    getParentIdPathToId(id: string): string[];
}

export class IdTitleNode implements IIdTitleNode {
    id: string;
    title: string;
    comment: string;
    active: boolean;
    children: IdTitleNode[];
    attributes: Dictionary<string>;

    constructor();
    constructor(obj: IIdTitleNode);
    constructor(obj?: any) {
        this.id = obj && obj.id || "";
        this.title = obj && obj.title || "";
        this.comment = obj && obj.comment || "";
        this.active = obj && obj.active || false;
        this.children = Base.getTypedArray(obj ? obj.children : null, IdTitleNode);
        this.attributes = new Dictionary<string>();
        if (obj && obj.attributes) {
            for (const key in obj.attributes.values) {
                this.attributes.add(key, obj.attributes.values[key]);
            }
        }
    }

    static createIdTitleNode(id: string, title: string, children: IIdTitleNode[], active: boolean = true, attributes: { key: string, value: string }[] = null, comment: string = ""): IIdTitleNode {
        const result = new IdTitleNode();
        result.id = id;
        result.title = title;
        result.comment = comment;
        result.active = active;
        result.children = children;
        if (attributes) {
            if (attributes) {
                for (const item of attributes) {
                    result.attributes.add(item.key, item.value);
                }
            }
        }
        return result;
    }

    getTitle(): string {
        return this.title;
    }

    static getTitlesByIds(items: IIdTitleNode[], ids: string[]): string {
        if (!items || items.length < 1 || !ids || ids.length < 1) return "";
        const selectedItems = IdTitleNode.getAsList(items).filter(i => ids.includes(i.id));
        if (selectedItems.length < 1) return "";
        return selectedItems.map(i => i.title).join(", ");
    }
    
    getAttributeValue(key: string): string {
        return this.attributes.get(key);
    }

    hasChildren(): boolean {
        return this.children.length > 0;
    }

    getById(id: string): IIdTitleNode {
        if (id === this.id) return this;
        for (const child of this.children) {
            const result = child.getById(id);
            if (!result) continue;
            return result;
        }
        return null;
    }

    static getById(items: IIdTitleNode[], id: string): IIdTitleNode {
        if (!items || items.length < 1) return null;
        for (const item of items) {
            const result = item.getById(id);
            if (!result) continue;
            return result;
        }
        return null;
    }

    getByIds(ids: string[]): IIdTitleNode[] {
        let result: IIdTitleNode[] = [];
        if (ids.indexOf(this.id) > -1) {
            result.push(this);
        }
        for (const child of this.children) {
            result = result.concat(child.getByIds(ids));
        }
        return result;
    }

    static getByIds(items: IIdTitleNode[], ids: string[]): IIdTitleNode[] {
        let result: IIdTitleNode[] = [];
        if (!items || items.length < 1 || !ids || ids.length < 1) return result;
        for (const item of items) {
            result = result.concat(item.getByIds(ids));
        }
        return result;
    }

    getFirstActiveNode(): IIdTitleNode {
        if (this.active) return this;
        for (const child of this.children) {
            const result = child.getFirstActiveNode();
            if (!result) continue;
            return result;
        }
        return null;
    }

    static getFirstActiveNode(items: IIdTitleNode[]): IIdTitleNode {
        if (!items || items.length < 1) return null;
        for (const item of items) {
            const result = item.getFirstActiveNode();
            if (!result) continue;
            return result;
        }
        return null;
    }

    getChildIds(): string[] {
        let result = this.children.map(i => i.id);
        for (const child of this.children) {
            result = result.concat(child.getChildIds());
        }
        return result;
    }

    static getChildIds(items: IIdTitleNode[]): string[] {
        if (!items || items.length < 1) return [];
        let result: string[] = [];
        for (const item of items) {
            result = result.concat(item.getChildIds());
        }
        return result;
    }

    getActiveChildIds(): string[] {
        let result = this.children.filter(i => i.active).map(i => i.id);
        for (const child of this.children) {
            result = result.concat(child.getChildIds());
        }
        return result;
    }

    static getActiveChildIds(items: IIdTitleNode[]): string[] {
        if (!items || items.length < 1) return [];
        let result: string[] = [];
        for (const item of items) {
            result = result.concat(item.getActiveChildIds());
        }
        return result;
    }

    containsAllChildrenIds(ids: string[]): boolean {
        if (!ids || ids.length < this.children.length) return false;
        return this.children.filter(i => ids.indexOf(i.id) > -1).length === this.children.length;
    }
    
    containsSomeChildrenIds(ids: string[]): boolean {
        if (!ids) return false;
        return this.children.filter(i => ids.indexOf(i.id) > -1).length > 0;
    }
        
    setHierarchialTitle(prefix: string) {
        this.title = (prefix ? prefix + " / " : "") + this.title;
        for (const child of this.children) {
            child.setHierarchialTitle(this.title);
        }
    }

    getChildIdPath(): string[] {
        let result = [this.id];
        for (let i = 0; i < this.children.length; i++) {
            result = result.concat(this.children[i].getChildIdPath());
        }
        return result;
    }

    getParentIdPathToId(id: string): string[] {
        if (!id) return null;
        if (this.id === id) return [];
        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            const childResult = child.getParentIdPathToId(id);
            if (childResult) {
                childResult.push(this.id);
                return childResult;
            }
        }
        return null;
    }

    static sortUIdTitleNodes(items: IIdTitleNode[]): IIdTitleNode[] {
        if (!items && items.length < 2) return items;
        const result = items.sort((a: IIdTitleNode, b: IIdTitleNode) => { return (!a || !b) ? 0 : (Base.strCompare(a.title, b.title)); });
        for (const item of result) {
            item.children = IdTitleNode.sortUIdTitleNodes(item.children);
        }
        return result;
    }
    
    static setHierarchialTitles(items: IIdTitleNode[]) {
        for (const item of items) {
            item.setHierarchialTitle(null);
        }
    }

    removeIds(ids: string[]): IIdTitleNode {
        if (ids.indexOf(this.id) > -1) {
            return null;
        }
        const result = new IdTitleNode(this);
        result.children = [];
        for (const child of this.children) {
            const newChild = child.removeIds(ids);
            if (!newChild) continue;
            result.children.push(newChild);
        }
        return result;
    }

    static removeIds(items: IIdTitleNode[], ids: string[]): IIdTitleNode[] {
        const result: IIdTitleNode[] = [];
        for (const item of items) {
            const newItem = item.removeIds(ids);
            if (!newItem) continue;
            result.push(newItem);
        }
        return result;
    }

    getAsList(list: IIdTitleNode[]): IIdTitleNode[] {
        list.push(this);
        if (this.children.length < 1) return list;
        for (const child of this.children) {
            child.getAsList(list);
        }
        return list;
    }

    static getAsList(nodes: IIdTitleNode[]): IIdTitleNode[] {
        if (!nodes || nodes.length < 1) return [];
        const result: IIdTitleNode[] = [];
        for (const node of nodes) {
            node.getAsList(result);
        }
        return result;
    }

    static equalIdsInArrays(a: IIdTitleNode[], b: IIdTitleNode[]): boolean {
        return Base.equalStrArrays(IdTitleNode.getAsList(a).map(i => i.id), IdTitleNode.getAsList(b).map(i => i.id));
    }

    static equalIds(a: IIdTitleNode[], b: IIdTitleNode[]): boolean {
        return Base.equalStrArrays(IdTitleNode.getChildIds(a ?? []), IdTitleNode.getChildIds(b ?? []));
    }

    static keepAvailableSelectedIds(items: IIdTitleNode[], selectedIds: string[], allValue: string = Base.emptyGuid): string[] {
        let result = selectedIds.slice(0);
        if (result.length > 0 && result[0] !== allValue) {
            const ids = IdTitleNode.getAsList(items).map(i => i.id);
            result = result.filter(i => ids.indexOf(i) > -1);
        }
        if (!result.length && IdTitleNode.getById(items, allValue)) {
            result.push(allValue);
        }
        return result;
    }

    static getActiveCount(items: IIdTitleNode[]): number {
        if (!items || items.length < 1) return 0;
        return IdTitleNode.getAsList(items).filter(i => i.active).length;
    }
}